import { Vue } from "vue-property-decorator"
import { Module } from "vuex"
import { BaseState } from "./index"
import User from "@/types/user"
import { CaseAudit } from "@/types/caseAudit"
import CaseFile from "@/types/caseFile"
import { Case, CaseDetails, Area, Media, RecordAction, FieldInstance, TreeNode, CaseField,
	Tag, Status, Severity, CaseError } from "@/types"
import { CaseEvent, CaseEvents } from "@/types/originatingEvents"
import { TemplateDetails } from "@/types/templateDetails"
import api from "@/services/api"
import eventsApi from "@/services/api.event"
import viewsService, { ViewsService } from "@/services/viewsService"
import { cascadeListChange, applyTemplate, updateCaseDetails, updateCaseFields } from "@/scripts/caseDetails"
import { InProgressSaveDetails } from "@/types/inProgressSaveDetails"
import { PagedData } from "@/types/pagedData"
import { TenantFeatures } from "@/types/tenantFeatures"
import { RecordFieldsUpdateDetails } from "@/types/caseUpdate"

export interface CasesState {
	loadingCases: Boolean;
	caseListLoadError: string | null;
	cases: PagedData<Case> | null;
	caseErrors: CaseError[];
	activeViewId: string | null;
	currentCase: CaseDetails | null;
	statuses: Status[] | null;
	severities: Severity[] | null;
	currentTemplate: TemplateDetails | null;
	areas: TreeNode<Area>[] | null;
	areaDictionary: Map<string, string>;
	categories: TemplateDetails[] | null;
	categoriesLoadPromise: Promise<any> | null;
	currentCaseAudit: CaseAudit[];
	currentCaseFiles: CaseFile[];
	pendingUploadFiles: any[];
	previewFile: any;
	previewKeyMedia: any;
	showCCEvent: CaseEvent | null;
	caseEvents: CaseEvents | null;
	actions: RecordAction[];
	caseFieldDetails: any;
	viewsService: ViewsService;
	caseToast: string | null;
	pendingRefresh: Set<string>;
	fieldLockInterval: any;
	fieldLockMilliseconds: number;
	inProgressSaveDetails: InProgressSaveDetails | null;
	caseFieldLastUpdatedLocally: { [fieldId: string]: Date };
};

export const state: CasesState = {
	loadingCases: false,
	caseListLoadError: null,
	cases: null,
	caseErrors: [],
	activeViewId: null,
	currentCase: null,
	currentTemplate: null,
	statuses: null,
	severities: null,
	areas: null,
	areaDictionary: new Map(),
	categories: null,
	categoriesLoadPromise: null,
	currentCaseAudit: [],
	currentCaseFiles: [],
	pendingUploadFiles: [],
	previewFile: null,
	previewKeyMedia: null,
	showCCEvent: null,
	caseEvents: null,
	actions: [],
	caseFieldDetails: null,
	viewsService: viewsService,
	caseToast: null,
	pendingRefresh: new Set<string>(),
	fieldLockInterval: null,
	fieldLockMilliseconds: 30000,
	inProgressSaveDetails: null,
	caseFieldLastUpdatedLocally: {}
}

const newCase = (state: CasesState): CaseDetails => {
	let defaultStatus = null
	if (state.statuses && state.statuses.length > 0) {
		defaultStatus = state.statuses.find(s => s.default)
		if (!defaultStatus) {
			defaultStatus = state.statuses[0]
		}
	}

	return {
		fields: {},
		sections: [],
		title: "",
		assigneeId: null,
		severityId: state.severities && state.severities.length > 0 ? state.severities![0].id : "",
		severityTitle: state.severities && state.severities.length > 0 ? state.severities![0].title : "",
		severityLevel: state.severities && state.severities.length > 0 ? state.severities![0].level : 1,
		statusId: defaultStatus ? defaultStatus.id : "",
		statusTitle: defaultStatus ? defaultStatus.title : "",
		cadenceDetails: null,
		templateId: null,
		categoryTitle: null,
		openedByUser: null,
		openedByUserId: null,
		id: null,
		caseNumber: null,
		isSensitive: false,
		areas: [],
		pendingTeams: [],
		users: [],
		summary: "",
		viewingUsers: [],
		isCase: false,
		isClosed: false,
		actions: [],
		links: [],
		caseFieldDetails: null,
		permissions: null,
		tags: [],
		cadenceEnabled: false,
		hiddenFromInsights: false
	} as CaseDetails
}

const filterTree = (filter: string | null, tree: TreeNode<Area>, nodeVisibility: boolean | null) => {
	const inFilter = !filter || tree.node.title.toLowerCase().indexOf(filter) > -1

	let filteredCount = 0
	if (tree.children) {
		tree.children.forEach(c => filteredCount += filterTree(filter, c, nodeVisibility))
	}

	if (inFilter) {
		filteredCount++
	}

	Vue.set(tree, "visible", (nodeVisibility !== null && tree.node.parentId !== null) ? nodeVisibility : (inFilter || filteredCount > 0))
	return filteredCount
}

export const cases: Module<CasesState, BaseState> = {
	namespaced: true,
	state,
	getters: {
		getLoadingCases: state => state.loadingCases,
		getCaseListLoadingError: state => state.caseListLoadError,
		getCases: state => state.cases,
		getCurrentCase: state => state.currentCase,
		getSeverities: state => state.severities,
		getStatuses: state => state.statuses,
		getCadenceDetails: state => state.currentCase!.cadenceDetails,
		getCategories: state => state.categories,
		getCreatableCategories: (state, getters, rootState) => {
			if (!state.categories || !rootState.me) return []

			return state.categories.filter(template => {
				if (template.preventManualCreation) {
					return false
				}

				if (!template.permissions) {
					return true
				}

				if (template.alwaysPromoted) {
					return template.permissions.case.edit.length === 0 ||
						template.permissions.case.edit.some(e => rootState.me!.teams.some((mt: any) => mt.id === e.id))
				}

				return template.permissions.case.edit.length === 0 ||
					template.permissions.case.edit.some(e => rootState.me!.teams.some((mt: any) => mt.id === e.id)) ||
					template.permissions.record.edit.length === 0 ||
					template.permissions.record.edit.some(e => rootState.me!.teams.some((mt: any) => mt.id === e.id))
			})
		},
		getCurrentCaseAudit: state => state.currentCaseAudit,
		getCurrentCaseFiles: state => state.currentCaseFiles,
		getNumberOfFilesAwaitingUpload: state => state.pendingUploadFiles.length,
		getPendingUploadFiles: state => state.pendingUploadFiles,
		getPreviewFile: state => state.previewFile,
		getPreviewKeyMedia: state => state.previewKeyMedia,
		getShowCCEvent: state => state.showCCEvent,
		getCaseFieldDetails: state => state.caseFieldDetails,
		getShowField: (state) => {
			return (fieldKey: string) => {
				if (!state.currentTemplate) return false
				if (!state.currentTemplate.hiddenFields) return true
				return !state.currentTemplate.hiddenFields!.includes(fieldKey)
			}
		},
		getBlankCase: (state) => () => newCase(state),
		getCurrentTemplate: (state) => state.currentTemplate
	},
	mutations: {
		setCurrentCase: (state: CasesState, currentCase: CaseDetails) => state.currentCase = currentCase,
		setActiveViewId: (state: CasesState, viewId: string) => state.activeViewId = viewId,
		setCurrentCaseId: (state: CasesState, caseId: string) => {
			if (state.currentCase) {
				state.currentCase.id = caseId
			}
		},
		setCurrentCaseNew: (state: CasesState) => {
			state.currentTemplate = null
			state.currentCase = newCase(state)

			state.caseEvents = null
			state.currentCaseFiles = []
		},
		setCurrentCaseTemplate: (state, template: TemplateDetails) => {
			if (state.currentCase) {
				applyTemplate(state.currentCase, template)
			}
		},
		setCurrentCaseTags: (state, tags: Tag[]) => {
			if (state.currentCase) {
				state.currentCase.tags = tags
			}
		},
		setCaseErrors: (state, errorList: Error[]) => state.caseErrors = errorList,
		setLoadingCases: (state: CasesState, loading: boolean) => state.loadingCases = loading,
		setListLoadingError: (state: CasesState, error: string | null) => state.caseListLoadError = error,
		setCaseList: (state: CasesState, caseList: PagedData<Case>) => state.cases = caseList,
		setStatuses: (state: CasesState, statuses: Status[]) => state.statuses = statuses,
		setSeverities: (state: CasesState, severities: Severity[]) => state.severities = severities,
		setCurrentTemplate: (state: CasesState, template: TemplateDetails | null) => state.currentTemplate = template,
		setAreas: (state: CasesState, areas: TreeNode<Area>[]) => {
			state.areas = areas
			if (state.areas) {
				state.areas.forEach(c => filterTree(null, c, false))
			}
		},
		setAreaDictionary: (state: CasesState, areaDictionary : Map<string, string>) => {
			state.areaDictionary = areaDictionary
		},
		setCategories: (state: CasesState, categories: TemplateDetails[]) => state.categories = categories,
		setCategoriesLoadPromise: (state: CasesState, promise: Promise<any>) => state.categoriesLoadPromise = promise,
		setAudit: (state: CasesState, currentCaseAudit: CaseAudit[]) => state.currentCaseAudit = currentCaseAudit,
		setCurrentCaseFiles: (state: CasesState, currentCaseFiles: CaseFile[]) => state.currentCaseFiles = currentCaseFiles,
		addFilesAwaitingUpload: (state: CasesState, filesToUpload: any[]) => {
			// state.pendingUploadFiles = filesToUpload
			filesToUpload.forEach(file => {
				state.pendingUploadFiles.push(file)
			})
		},
		setFileNoLongerAwaitingUpload: (state: CasesState, file: string) => {
			while (true) {
				const fileNameIndex = state.pendingUploadFiles.findIndex(uploadFilename => uploadFilename === file)
				if (fileNameIndex > -1) {
					state.pendingUploadFiles.splice(fileNameIndex, 1)
				} else {
					break
				}
			}
		},
		clearPendingFiles: (state: CasesState) => {
			state.pendingUploadFiles = []
		},
		updateCurrentCase: (state: CasesState, updatedCase: CaseDetails) => {
			if (!state.currentCase) {
				return
			}

			updateCaseDetails(state.currentCase, updatedCase)
		},
		updateCurrentCaseFields: (state: CasesState, updateDetails: RecordFieldsUpdateDetails) => {
			if (!state.currentCase || updateDetails.caseId !== state.currentCase.id) {
				return
			}

			updateCaseFields(state.currentCase, updateDetails.updatedFields, updateDetails.currentUserId,
				updateDetails.isInProgressUpdate,
				state.caseFieldLastUpdatedLocally,
				updateDetails.changedByUserId)
		},
		updateCurrentCaseUsers: (state: CasesState, viewingUsers: User[]) => {
			if (state.currentCase) {
				state.currentCase.viewingUsers = viewingUsers
			}
		},
		setPreviewFile: (state: CasesState, file: any) => {
			state.previewFile = file
		},
		setPreviewKeyMedia: (state: CasesState, keyMedia: Media) => state.previewKeyMedia = keyMedia,
		setShowCCEvent: (state: CasesState, event: any) => state.showCCEvent = event,
		setCaseEvents: (state: CasesState, caseEvents: CaseEvents) => state.caseEvents = caseEvents,
		setCaseFieldDetails: (state: CasesState, details: any) => {
			if (state.caseFieldDetails) {
				state.caseFieldDetails.definition.fields.forEach((field: FieldInstance) => {
					field.value = null
					field.fromData = false
				})
			}

			state.caseFieldDetails = details
		},
		setAreaFilter: (state, filter: string) => {
			if (!state.areas) {
				return
			}

			const lowerFilter = filter.toLowerCase()
			state.areas.forEach(c => filterTree(lowerFilter, c, null))
		},
		setCaseToast: (state, message: string | null) => state.caseToast = message,
		cascadeListChange: (state, { caseDetails, caseField, newValue, getFields }: { caseDetails?: CaseDetails, caseField: CaseField, newValue: any, getFields: boolean }) => {
			const cascadeCase = caseDetails || state.currentCase

			if (!cascadeCase) return
			cascadeListChange(cascadeCase, caseField, newValue, getFields)
		},
		addPendingRefresh: (state, itemType: string) => state.pendingRefresh.add(itemType),
		removePendingRefresh: (state, itemType: string) => state.pendingRefresh.delete(itemType),
		setCaseFieldLock: (state, { caseField }: { caseField: CaseField }) => {
			caseField.isLocked = true
		},
		setInProgressSaveDetails: (state, details: InProgressSaveDetails) => {
			if (details && details.fieldId && details.text) {
				state.inProgressSaveDetails = details
			}
		},
		clearInProgressSaveDetails: (state) => {
			state.inProgressSaveDetails = null
		},
		setCaseFieldLastUpdatedLocally: (state, { fieldId, date }: { fieldId: string, date: Date }) => {
			if (fieldId && date) {
				state.caseFieldLastUpdatedLocally[fieldId] = date
			}
		},
		clearCaseFieldLastUpdatedLocally: (state) => {
			state.caseFieldLastUpdatedLocally = {}
		}
	},
	actions: {
		caseCreate: async function ({ commit, getters }, caseDetails: CaseDetails) {
			let errorList = []
			const currentCase = caseDetails

			// const currentCaseWithoutFields = cloneDeep(currentCase) as any
			// delete currentCaseWithoutFields.fields

			const newCase = await api.saveCase(currentCase)

			// Do not use foreach here - we need to await!
			// const fieldIds = Object.keys(currentCase.fields!)
			// for (let fieldIndex = 0; fieldIndex < fieldIds.length; fieldIndex++) {
			// 	const fieldId = fieldIds[fieldIndex]
			// 	const field = currentCase.fields![fieldId]

			// 	if (field.value != null) {
			// 		try {
			// 			if (field.field.extensions && field.field.extensions!.calculationType !== undefined && field.field.dataType !== "calculated") {
			// 				await api.setFieldCalculated(newCase.id!, field.field.id!, false)
			// 			}

			// 			if (field.field.dataType === "list-data") {
			// 				for (let index = 0; index < field.value.length; index++) {
			// 					const value = field.value[index]
			// 					await api.updateCaseDataField(newCase.id!, field.field.id!, 1, value)
			// 				}
			// 			} else if (field.field.dataType !== "calculated") {
			// 				await api.saveCaseField(newCase.id!, field.field.id!, field.value)
			// 			}
			// 		} catch (e) {
			// 			const error: CaseError = {
			// 				field: field.field,
			// 				message: e
			// 			}
			// 			errorList.push(error)
			// 		}
			// 	}
			// }

			return {
				created: newCase,
				errors: []
			}
		},
		async casesLoad ({ commit }) {
			commit("setLoadingCases", true)

			try {
				commit("setCaseList", await api.caseList())
				commit("setListLoadingError", null)
			} catch (err) {
				commit("setListLoadingError", err)
			}

			commit("setLoadingCases", false)
		},
		async detailsLoad ({ commit, state, rootGetters }) {
			const load = []

			if (!state.statuses || state.statuses.length === 0) {
				load.push(api.statuses().then((statuses) => {
					commit("setStatuses", statuses)
				}))
			}

			if (!state.statuses || state.statuses.length === 0) {
				load.push(api.severities().then((severities) => {
					commit("setSeverities", severities)
				}))
			}

			if (!state.categories || state.categories.length === 0 || state.pendingRefresh.has("template")) {
				load.push(api.categories().then((categories) => {
					commit("setCategories", categories)
					commit("removePendingRefresh", "template")
				}))
			}

			const excludeAreas = rootGetters.isFeatureEnabled(TenantFeatures.AreaScaling)

			if (!excludeAreas && (!state.areas || state.areas.length === 0)) {
				load.push(api.areas().then((areas) => {
					commit("setAreas", areas)
				}))
			}

			if (load.length > 0) {
				await Promise.all(load)
			}
		},
		async severitiesLoad ({ state, commit }) {
			if (!state.severities || state.severities.length === 0) {
				await api.severities().then((severities) => {
					commit("setSeverities", severities)
				})
			}
		},
		async detailsUnload ({ commit, state }) {
			commit("setStatuses", [])
			commit("setSeverities", [])
			commit("setCategories", null)
			commit("setAreas", [])
			commit("clearCaseFieldLastUpdatedLocally")
		},
		async caseOpen ({ commit, dispatch, state }, caseId: string) {
			await dispatch("detailsLoad")

			const caseDetails = await api.caseDetails(caseId)
			if (caseDetails.fields) {
				Object.keys(caseDetails.fields).forEach(fieldId => {
					caseDetails.fields![fieldId].oldValue = caseDetails.fields![fieldId].value
				})
			}

			try {
				commit("setCurrentTemplate", await api.templateDetails(caseDetails.templateId!))
			} catch {
				commit("setCurrentTemplate", {
					hiddenFields: []
				})
			}

			commit("setCurrentCase", caseDetails)
		},
		async caseClear ({ commit, dispatch }, caseId: string) {
			commit("setCurrentCase", null)
			commit("setAudit", [])
			commit("clearPendingFiles")
			commit("setCurrentCaseFiles", [])
			commit("setCaseEvents", null)
		},
		async caseRefresh ({ commit, getters }) {
			// const currentCase = getters.getCurrentCase
			// if (currentCase.id) {
			// 	const caseDetails = await api.caseDetails(currentCase.caseNumber)

			// 	commit("updateCurrentCase", caseDetails)
			// }
		},
		async refreshCategories ({ commit, state }) {
			if (state.categoriesLoadPromise) await state.categoriesLoadPromise

			try {
				const loadCategoriesPromise = api.categories()
				commit("setCategoriesLoadPromise", loadCategoriesPromise)

				const loadedCategories = await loadCategoriesPromise
				commit("setCategories", loadedCategories)
				commit("setCategoriesLoadPromise", null)
			} catch (e) {
				console.log(e)
				commit("setCategoriesLoadPromise", null)
			}
		},
		async caseAuditLoad ({ commit, getters }, { lastUpdated }: { lastUpdated: string }) {
			const currentCase = getters.getCurrentCase
			if (currentCase.id) {
				commit("setAudit", await api.history(currentCase.id, lastUpdated))
			}
		},
		async caseAuditLoadAndAdd ({ commit, getters }, { lastUpdated, currentAudit }: { lastUpdated: string, currentAudit: CaseAudit[] | null }) {
			const currentCase = getters.getCurrentCase
			if (currentCase.id) {
				const newEntries = await api.history(currentCase.id!, lastUpdated)
				if (currentAudit && currentAudit.length > 0 && newEntries && newEntries.length > 0) {
					let newHistory = [
						...newEntries,
						...currentAudit
					]
					commit("setAudit", newHistory)
				}
			}
		},
		async caseFilesLoad ({ commit, getters }) {
			const currentCase = getters.getCurrentCase
			if (currentCase.id) {
				commit("setCurrentCaseFiles", await api.caseFiles(currentCase.id))
			}
		},
		async uploadFiles ({ commit, getters, state, dispatch }, { fieldId, uploadFiles }) {
			if (!state.currentCase) {
				return
			}

			const caseId = state.currentCase.id
			commit("addFilesAwaitingUpload", uploadFiles)
			if (!caseId) {
				return
			}

			state.pendingUploadFiles.forEach((uploadFile: any) => {
				if (uploadFile.uploadRun) {
					return
				}
				uploadFile.uploadRun = true

				const uploadFormData = new FormData()
				uploadFormData.append("file", uploadFile.fileData)

				uploadFile.uploading = true
				uploadFile.uploadPercentage = 0

				api.uploadFiles(caseId, fieldId || uploadFile.fieldId, uploadFormData, (e: ProgressEvent) => {
					uploadFile.uploadPercentage = Math.round(e.loaded * 100 / e.total)
				},
				(errorMessage: string) => {
					uploadFile.failed = true
					uploadFile.error = errorMessage
					uploadFile.uploading = false
					uploadFile.success = false

					console.log("Failed.")
				},
				() => {
					if (!uploadFile.failed) {
						uploadFile.uploading = false
						uploadFile.success = true

						commit("setFileNoLongerAwaitingUpload", uploadFile)

						setTimeout(() => {
							dispatch("caseFilesLoad")
						}, 10)
					}
				})
			})
		},
		async entrySetKey ({ state }, { eventId, eventRecordId, key }: { eventId: string, eventRecordId: number, key: boolean }) {
			if (!state.currentCase) return

			eventsApi.eventRecordMarkKey(state.currentCase!.id!, eventId, eventRecordId, key)
		},
		async mediaSetKey ({ state }, { clipId, key }: { clipId: string, key: boolean }) {
			if (!state.currentCase) return

			eventsApi.mediaMarkKey(state.currentCase!.id!, clipId, key)
		},

		setFieldLockPolling ({ state, dispatch }, { caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) {
			dispatch("cancelFieldLockPolling", { caseDetails, fieldId })

			let caseField: any

			if (caseDetails!.id) {
				caseField = caseDetails.builtInFieldLocks ? caseDetails.builtInFieldLocks[fieldId] : caseDetails.fields[fieldId]

				let counter = 0
				if (caseField) {
					state.fieldLockInterval = setInterval(async () => {
						counter++
						await api.extendCaseFieldLock(<string>caseDetails!.id, fieldId)
					}, state.fieldLockMilliseconds)
				}
			}
		},

		cancelFieldLockPolling ({ state }, { caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) {
			let caseField: any

			if (caseDetails) {
				caseField = caseDetails.builtInFieldLocks ? caseDetails.builtInFieldLocks[fieldId] : caseDetails.fields[fieldId]

				if (caseField) {
					clearInterval(state.fieldLockInterval)
				}
			}
		},
		async fetchAreaDictionary ({ commit }) {
			const areaDictionary = await api.getAreaDictionary()
			commit("setAreaDictionary", areaDictionary)
		},
		async saveCaseField ({ state, commit }, { caseField, inProgressUpdate = false }: { caseField: CaseField, inProgressUpdate?: boolean }) {
			if (!state.currentCase?.id) return

			const fieldsPrepend = "fields."
			commit("setCaseFieldLastUpdatedLocally", { fieldId: fieldsPrepend + caseField.field.id!, date: new Date(Date.now()) })
			await api.saveCaseField(state.currentCase!.id!, caseField.field.id!, caseField.value, inProgressUpdate)
		},
		async saveCaseProperty ({ state, commit }, { caseColumnKey, newValue }: { caseColumnKey: string, newValue: string | null }) {
			if (!state.currentCase?.id) return

			commit("setCaseFieldLastUpdatedLocally", { fieldId: caseColumnKey, date: new Date(Date.now()) })
			await api.saveCaseProperty(state.currentCase!.id!, caseColumnKey, newValue)
		}
	}
}
